"use client";
import React, { useEffect, useState, useCallback, useMemo } from "react";
import Image from "next/image";
import ThankYouCheckboxCard from "./ui/Cards/ThankYouCheckboxCard";
import { getPromotedModels, getSimilarModels, submitLead } from "@/app/utilities/apis";
import { formatPrice, safeImageUrl } from '@/app/utilities/helper';
import { hasSubmittedLead, storeLeadSubmission } from '@/utils/leadTracking';
import Notification from './Notification';
import { getInsuranceEstimate } from '@/utils/insurance';
import { calculateRoadTax, StateRtoConfig } from '@/utils/rto';

interface Props {
    isOpen?: boolean;
    onClose?: () => void;
    modelId?: string | null;
    leadData?: any;
    model?: any;
}

const POPUP_CAR_LIMIT = 3; // Minimum cars shown; admin-promoted cars are never cut off.

// Flat model doc -> same shape getSimilarModels returns
const toPopupCar = (m: any) => ({
    brand: { _id: m.brand_id, name: m.brand_name, slug: m.brand_slug },
    model: { _id: m._id, name: m.name, slug: m.slug, detailed_slug: m.detailed_slug, image: m.featured_image },
    price_range: m.price_range ?? { base: null, top: null },
});

const getPromotedPopupCars = async (): Promise<any[]> => {
    try {
        const res: any = await getPromotedModels();
        return (Array.isArray(res?.data) ? res.data : []).map(toPopupCar);
    } catch {
        return [];
    }
};

const getCarId = (m: any): string => m._id || m.id || m.model?._id || m.model?.id || '';

const getCarName = (m: any): string => `${m.brand?.name || ''} ${m.model?.name || ''}`.trim();

const getProductName = (leadData: any): string => {
    const name = `${leadData?.brand || ''} ${leadData?.modelName || leadData?.model?.name || ''} ${leadData?.variantName || ''}`
        .replace(/\s+/g, ' ')
        .trim();
    return name || 'selected car';
};

const calculatePriceBreakdown = (leadData: any): { rto: number | null; insurance: number | null } => {
    try {
        const exShowroomPrice =
            leadData?.exShowroomPrice ??
            leadData?.price ??
            leadData?.price_range?.base ??
            null;

        if (!exShowroomPrice || typeof exShowroomPrice !== 'number') {
            return { rto: null, insurance: null };
        }

        // Calculate RTO if state data is available
        let rtoAmount: number | null = null;
        if (leadData?.locationData?.stateCode && leadData?.locationData?.rtoSlabs) {
            const stateConfig: StateRtoConfig = {
                stateCode: leadData.locationData.stateCode,
                rtoSlabs: leadData.locationData.rtoSlabs,
                secondVehicleTaxPercent: leadData.locationData.secondVehicleTaxPercent,
            };

            const rtoResult = calculateRoadTax({
                base: exShowroomPrice,
                engineCc: leadData?.engineCc || leadData?.engine_cc || 1200,
                fuelType: leadData?.fuelType || leadData?.fuel_type || 'PETROL',
                ownershipType: 'INDIVIDUAL',
                stateConfig,
                isSecondVehicle: false,
            });

            rtoAmount = rtoResult?.total || null;
        }

        // Calculate Insurance
        let insuranceAmount: number | null = null;
        const isEV = (leadData?.fuelType || leadData?.fuel_type || '').toUpperCase() === 'ELECTRIC';
        const power = isEV
            ? (leadData?.motorKw || leadData?.motor_kw || 40)
            : (leadData?.engineCc || leadData?.engine_cc || 1200);
        const zone: 'A' | 'B' = leadData?.locationData?.cityTier === 'Tier 1' ? 'A' : 'B';

        const insuranceResult = getInsuranceEstimate(
            exShowroomPrice,
            isEV,
            power,
            zone,
            isEV
        );

        insuranceAmount = insuranceResult?.total || null;

        return { rto: rtoAmount, insurance: insuranceAmount };
    } catch (error) {
        console.error('Error calculating price breakdown:', error);
        return { rto: null, insurance: null };
    }
};

const ThanksPopup: React.FC<Props> = ({ isOpen: isOpenProp, onClose, modelId, leadData, model }) => {
    const [internalOpen, setInternalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [similar, setSimilar] = useState<any[]>([]);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
    const [submitting, setSubmitting] = useState(false);
    const [offerChecked, setOfferChecked] = useState(true);
    const [notification, setNotification] = useState({ message: '', isError: false, visible: false });

    const showNotification = useCallback((message: string, isError = false) => {
        setNotification({ message, isError, visible: true });
        setTimeout(() => setNotification(prev => ({ ...prev, visible: false })), 3000);
    }, []);

    const isOpen = isOpenProp ?? internalOpen;
    const close = () => (onClose ? onClose() : setInternalOpen(false));
    const effectiveLeadData = useMemo(
        () => leadData ?? (typeof window !== 'undefined' ? (window as any).__LAST_LEAD_DATA__ : undefined) ?? {},
        [leadData]
    );
    const productName = useMemo(() => getProductName(effectiveLeadData), [effectiveLeadData]);
    const thankYouSubtitle = useMemo(() => model?.status === 'upcoming' && !model?.price_range?.base ? `Stay tuned for more updates` : `We'll contact you soon with ${productName} offers`, [productName]);

    const defaultSelectedId = useMemo(() => {
        const firstSelectable = similar.find((item: any) => {
            const id = getCarId(item);
            return id && !hasSubmittedLead(id);
        });
        return firstSelectable ? getCarId(firstSelectable) : '';
    }, [similar]);

    useEffect(() => {
        if (!isOpen || !modelId) return;

        const fetchSimilar = async () => {
            setLoading(true);
            try {
                const [res, promotedCars]: [any, any[]] = await Promise.all([
                    getSimilarModels(modelId, POPUP_CAR_LIMIT),
                    getPromotedPopupCars(),
                ]);
                const data = res?.data || res?.modelData || [];
                const similarCars = Array.isArray(data) ? data : [];
                if (!promotedCars.length) {
                    setSimilar(similarCars);
                    return;
                }
                const seen = new Set<string>([String(modelId)]);
                const dedupe = (car: any) => {
                    const id = getCarId(car);
                    if (!id || seen.has(id)) return false;
                    seen.add(id);
                    return true;
                };
                const promoted = promotedCars.filter(dedupe);
                const fill = similarCars.filter(dedupe).slice(0, Math.max(0, POPUP_CAR_LIMIT - promoted.length));
                setSimilar([...promoted, ...fill]);
            } catch (err) {
                console.error("Failed to fetch similar models", err);
                setSimilar([]);
            } finally {
                setLoading(false);
            }
        };

        fetchSimilar();
    }, [isOpen, modelId]);

    useEffect(() => {
        if (!isOpen || !defaultSelectedId) return;

        setSelectedIds(prev => {
            const next = new Set(prev);
            next.add(defaultSelectedId);
            return next;
        });
    }, [isOpen, defaultSelectedId]);

    const handleCheckboxChange = (id: string, checked: boolean) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (checked) {
                next.add(id);
            } else {
                next.delete(id);
            }
            return next;
        });
    };

    const handleSubmit = async () => {
        if (selectedIds.size === 0) return;

        setSubmitting(true);
        try {
            const ids = Array.from(selectedIds).filter(id => !hasSubmittedLead(id));
            const baseLead = leadData ?? (typeof window !== 'undefined' ? (window as any).__LAST_LEAD_DATA__ : undefined) ?? {};

            const promises = ids.map(id =>
                submitLead({ ...baseLead, car_id: id, interested_car_ids: ids })
                    .then(res => ({ id, res }))
                    .catch(err => ({ id, err }))
            );

            const results = await Promise.all(promises);
            const successCount = results.filter((r: any) => r.res?.status === 'success').length;

            // Store successful submissions
            results.forEach((r: any) => {
                if (r.res?.status === 'success' && r.id) {
                    const car = similar.find(m => getCarId(m) === r.id);
                    storeLeadSubmission(r.id, car ? getCarName(car) : '');
                }
            });

            if (successCount === ids.length) {
                showNotification('We’ll contact you soon with offers for the selected cars.');
                close();
            } else if (successCount > 0) {
                showNotification(`We’ll contact you soon with offers for ${successCount} of the selected cars. Some failed.`);
                close();
            } else {
                showNotification('Failed to submit selected cars. Please try again.', true);
            }
        } catch (err) {
            console.error('Failed to submit selected cars', err);
            showNotification('Failed to submit. Please try again.', true);
        } finally {
            setSubmitting(false);
        }
    };

    const isButtonDisabled = submitting || selectedIds.size === 0;

    return (
        <>
            {/* If no controlled prop provided, show the open button for backwards compatibility */}
            {typeof isOpenProp === "undefined" && (
                <button
                    onClick={() => setInternalOpen(true)}
                    className="bg-[#008C8B] text-white px-6 py-3 rounded"
                >
                    Open Thanks Popup
                </button>
            )}

            {isOpen && (
                <div
                    className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100]"
                    onClick={close}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        className="bg-white rounded-[24px] w-[90%] sm:w-[450px] relative"
                    >
                        <button
                            onClick={close}
                            className="absolute top-3 right-3 z-20 w-[30px] h-[30px] p-[8px] bg-[#90CCDB26] rounded-full flex items-center justify-center cusror-pointer"
                        >
                            <Image
                                src="https://static-asset.tractorjunction.com/cj/popup-close.svg"
                                alt="close icons"
                                width={32}
                                height={32}
                                className="w-8 h-8"
                            />
                        </button>

                        <div className="rounded-t-[24px] px-4 pt-6 pb-2 pr-12 text-center">
                            <p className="font-roboto font-extrabold text-[24px] text-[#101828] leading-[1] tracking-[-0.01em]">
                                Thank You!
                            </p>
                            <p className="font-roboto font-medium text-[13px] md:text-[16px] text-[#475467] text-center mt-2">
                                {thankYouSubtitle}
                            </p>
                        </div>

                        {similar.length > 0 && (
                            <>
                                <div className="px-3 md:px-4 py-4">
                                    <p className="font-roboto font-bold text-[16px] leading-[1] tracking-[-0.66px] mb-4">
                                        You may also be Interested in
                                    </p>

                                    <div className="mb-4">
                                        <div className="flex flex-row items-start overflow-x-auto m-0 gap-2">
                                            {loading && <div className="p-4">Loading...</div>}

                                            {!loading && similar.map((m: any, index: number) => {
                                                const id = getCarId(m);
                                                return (
                                                    <ThankYouCheckboxCard
                                                        key={id || index}
                                                        brand={m.brand?.name || ''}
                                                        model={m.model?.name || ''}
                                                        price={m?.price_range?.base ? String(formatPrice(Number(m.price_range.base), { includeUnit: true })) : "Price on request"}
                                                        image={safeImageUrl(m.model?.image)}
                                                        checked={selectedIds.has(id)}
                                                        onChange={(checked) => handleCheckboxChange(id, checked)}
                                                        disabled={id ? hasSubmittedLead(id) : true}
                                                    />
                                                );
                                            })}
                                        </div>
                                    </div>

                                    <div className="flex items-center mb-4">
                                        <input
                                            type="checkbox"
                                            id="yes"
                                            checked={offerChecked}
                                            onChange={() => setOfferChecked((s) => !s)}
                                            className="mr-2 w-4 h-4 accent-black focus:ring-0"
                                        />
                                        <label htmlFor="yes" className="text-[11px] md:text-[12px] text-[#6B7280]">
                                            Yes, I would like to receive offer for other similar cars.
                                        </label>
                                    </div>

                                    <button
                                        onClick={handleSubmit}
                                        disabled={isButtonDisabled}
                                        className={`w-full py-3 rounded bg-[#008C8B] text-white ${isButtonDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'
                                            }`}
                                    >
                                        {submitting ? 'Submitting...' : 'Receive similar offers'}
                                    </button>

                                </div>
                            </>
                        )}

                    </div>
                </div>
            )}
            <Notification message={notification.message} isVisible={notification.visible} isError={notification.isError} />
        </>
    );
};

export default ThanksPopup;